import history from 'instantsearch.js/es/lib/routers/history';
import { normalizeRefinements } from './algolia-helpers';

const convertTextToSlug = text =>
    text
        .replace(/-/g, '---')
        .toLowerCase()
        .split(' ')
        .map(encodeURIComponent)
        .join('-')
        .replace(/-%26-/g, '-and-');

const convertSlugToText = slug =>
    slug
        .replace(/-and-/g, '-%26-')
        .split('-')
        .map(decodeURIComponent)
        .join(' ')
        .replace(/ {3}/g, '-');

const getQueryString = (
    qsModule,
    query = '',
    page = 1,
    categories = [],
    collections = [],
    favorites = null,
    newFilter = null
) => {
    const queryParameters = {};

    if (favorites) {
        queryParameters.favorites = favorites;
    }

    if (newFilter) {
        queryParameters.new = newFilter;
    }

    if (categories.length > 0) {
        queryParameters.categories = categories.map(encodeURIComponent);
    }

    if (collections.length > 0) {
        queryParameters.collections = collections.map(encodeURIComponent);
    }

    if (query.length > 0) {
        queryParameters.query = encodeURIComponent(query);
    }

    if (page !== 1) {
        queryParameters.page = page;
    }

    return qsModule.stringify(queryParameters, {
        addQueryPrefix: true,
        arrayFormat: 'repeat',
    });
};

const router = history({
    windowTitle({ category }) {
        //if (category) {
        //    return `Explore ${category} | McKinnon and Harris`;
        //}
        //return 'Explore Outdoor Furniture | McKinnon and Harris';
    },

    createURL({ qsModule, routeState, location }) {
        const urlParts = location.href.match(/^(.*?)\/furniture/);
        const baseUrl = `${urlParts ? urlParts[1] : ''}/`;
        let queryString = '';

        // If "Our Favorites" is selected, make that the URL route
        if (routeState.favorites) {
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.categories,
                routeState.collections,
                null,
                routeState.new
            );

            return `${baseUrl}furniture/favorites/${queryString}`;
        }

        // If "New" is selected, make that the URL route
        if (routeState.new) {
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.categories,
                routeState.collections
            );

            return `${baseUrl}furniture/new/${queryString}`;
        }

        // If category filters exist, make that the URL route
        if (routeState.categories && routeState.categories.length > 0) {
            const categoryPath = convertTextToSlug(routeState.categories[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                (routeState.categories || []).slice(1),
                routeState.collections
            );

            return `${baseUrl}furniture/category/${categoryPath}${queryString}`;
        }

        // Make collection the URL route if category filters don't exist
        if (routeState.collections && routeState.collections.length > 0) {
            const collectionPath = convertTextToSlug(routeState.collections[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.categories,
                (routeState.collections || []).slice(1)
            );

            return `${baseUrl}furniture/collection/${collectionPath}${queryString}`;
        }

        // If neither category nor collection filters exist, don't use URL routing at all
        getQueryString(qsModule, routeState.query, routeState.page);
        return `${baseUrl}furniture/${queryString}`;
    },

    parseURL({ qsModule, location }) {
        const { query = '', page, categories = [], collections = [], new: newFilter } = qsModule.parse(
            location.search.slice(1)
        );

        const favoritesPathnameMatches = location.pathname.match(/favorites/);
        const newPathnameMatches = location.pathname.match(/new/);

        const categoryPathnameMatches = location.pathname.match(/category\/(.*?)\/?$/);
        const mainCategory = convertSlugToText((categoryPathnameMatches && categoryPathnameMatches[1]) || '');
        const otherCategories = Array.isArray(categories) ? categories : [categories].filter(Boolean);

        const collectionPathnameMatches = location.pathname.match(/collection\/(.*?)\/?$/);
        const mainCollection = convertSlugToText((collectionPathnameMatches && collectionPathnameMatches[1]) || '');
        const otherCollections = Array.isArray(collections) ? collections : [collections].filter(Boolean);

        return {
            query: decodeURIComponent(query),
            page,
            favorites: !!favoritesPathnameMatches,
            new: !!newPathnameMatches || newFilter === '1',
            mainCategory,
            otherCategories: otherCategories.map(decodeURIComponent),
            mainCollection,
            otherCollections: otherCollections.map(decodeURIComponent),
        };
    },
});

const stateMapping = {
    stateToRoute(uiState) {
        uiState.refinementList = normalizeRefinements(uiState.refinementList || []);

        return {
            query: uiState.query,
            page: uiState.page,
            categories: uiState.refinementList && uiState.refinementList.categories,
            collections: uiState.refinementList && uiState.refinementList.collection,
            new: uiState.toggle && uiState.toggle.new && 1,
            favorites: uiState.toggle && uiState.toggle.favorites && 1,
        };
    },

    routeToState(routeState) {
        const state = {
            query: routeState.query,
            page: routeState.page,
            menu: {},
            toggle: {},
            refinementList: {
                collection: [],
                categories: [],
            },
        };

        if (routeState.favorites) {
            state.toggle.favorites = true;
        }

        if (routeState.new) {
            state.toggle.new = true;
        }

        if (routeState.mainCategory) {
            state.refinementList.categories.push(routeState.mainCategory);
        }

        if (routeState.otherCategories.length > 0) {
            state.refinementList.categories = state.refinementList.categories.concat(routeState.otherCategories);
        }

        if (routeState.mainCollection) {
            state.refinementList.collection.push(routeState.mainCollection);
        }

        if (routeState.otherCollections.length > 0) {
            state.refinementList.collection = state.refinementList.collection.concat(routeState.otherCollections);
        }

        return state;
    },
};

export const furnitureRouting = {
    router,
    stateMapping,
};
